import React from 'react';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime())
    ? dateStr
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const HOUR_LABELS = { bmo: 'Before Market Open', amc: 'After Market Close', dmh: 'During Market Hours' };

function formatHour(hour) {
  return HOUR_LABELS[hour] || 'Time TBD';
}

function formatPct(value, decimals = 2) {
  if (value == null) return '—';
  const n = Number(value);
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;
}

function formatNum(value, decimals = 2) {
  return value == null ? '—' : Number(value).toFixed(decimals);
}

const cellStyle = { padding: '6px 8px', fontSize: 12 };
const headerCellStyle = { ...cellStyle, color: 'var(--muted)', fontWeight: 600, textAlign: 'left' };

function MetricCell({ label, value }) {
  return (
    <div>
      <div style={{ color: 'var(--muted)', fontSize: 10 }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: 12 }}>{value}</div>
    </div>
  );
}

/**
 * Structured earnings/fundamentals card, shared between StockPage.js and
 * AnalystPage.js's chat thread. `data` is the Finnhub-derived shape
 * {earnings_calendar, eps_surprises, key_metrics} (or {error}/null).
 */
export default function EarningsFundamentalsCard({ data }) {
  if (!data || data.error) return null;

  const earningsCalendar = data.earnings_calendar || [];
  const epsSurprises = data.eps_surprises || [];
  const keyMetrics = data.key_metrics || {};

  const today = new Date().toISOString().slice(0, 10);
  const nextEarnings = earningsCalendar.find((ev) => ev.date >= today) || null;

  const hasSurprises = epsSurprises.length > 0;
  const hasMetrics = Object.values(keyMetrics).some((v) => v != null);

  if (!nextEarnings && !hasSurprises && !hasMetrics) return null;

  return (
    <div style={{
      marginTop: 8,
      border: '1px solid var(--border)',
      borderRadius: 10,
      background: 'var(--surface)',
      padding: 14,
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--text)' }}>
        📊 Earnings &amp; Fundamentals
      </div>

      {nextEarnings && (
        <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
            Next Earnings
          </div>
          <div style={{ fontWeight: 600 }}>{formatDate(nextEarnings.date)} · {formatHour(nextEarnings.hour)}</div>
          <div style={{ color: 'var(--muted)', marginTop: 2 }}>
            EPS estimate: {nextEarnings.eps_estimate != null ? `$${formatNum(nextEarnings.eps_estimate)}` : '—'}
          </div>
        </div>
      )}

      {hasSurprises && (
        <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
            EPS Surprise History
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 320 }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Period</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Actual</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Estimate</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Surprise</th>
                </tr>
              </thead>
              <tbody>
                {epsSurprises.slice(0, 6).map((row) => (
                  <tr key={row.period} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={cellStyle}>{row.period}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{row.eps_actual != null ? `$${formatNum(row.eps_actual)}` : '—'}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{row.eps_estimate != null ? `$${formatNum(row.eps_estimate)}` : '—'}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', color: row.surprise_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                      {formatPct(row.surprise_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasMetrics && (
        <div>
          <div style={{ color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
            Key Metrics
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
            <MetricCell label="P/E (TTM)" value={formatNum(keyMetrics.pe_ttm)} />
            <MetricCell label="EPS (TTM)" value={keyMetrics.eps_ttm != null ? `$${formatNum(keyMetrics.eps_ttm)}` : '—'} />
            <MetricCell label="Rev Growth YoY" value={formatPct(keyMetrics.revenue_growth_ttm_yoy)} />
            <MetricCell label="Gross Margin" value={formatPct(keyMetrics.gross_margin_ttm)} />
            <MetricCell label="Net Margin" value={formatPct(keyMetrics.net_margin_ttm)} />
            <MetricCell label="ROE (TTM)" value={formatPct(keyMetrics.roe_ttm)} />
            <MetricCell label="Debt/Equity" value={formatNum(keyMetrics.debt_to_equity)} />
            <MetricCell
              label="52W Range"
              value={
                keyMetrics['52_week_low'] != null && keyMetrics['52_week_high'] != null
                  ? `$${formatNum(keyMetrics['52_week_low'])} - $${formatNum(keyMetrics['52_week_high'])}`
                  : '—'
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
